import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import moment from "moment";

// Import Images
import logoDark from "assets/images/anjal-gold-logo.png";
import logoLight from "assets/images/anjal-gold-logo.png";
import {
  ContactInfo,
  fetchOrders,
  generateInvoicePDF,
  getContactInfo1,
  getOrderById,
  saveInvoice,
  sendInvoiceEmail,
} from "api/apis";
import { toast } from "react-toastify";

interface OrdersResponse {
  orders: any[];
  user: any[];
  totalPages: number;
  currentPage: number;
  count: number;
}

interface Address {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  zipCode?: string;
  zipcode?: string;
  state?: string;
}

interface PriceBreakdownItem {
  label: string;
  value: number;
  type: string;
  show?: boolean;
  valueType?: string;
  isCharge?: boolean;
  isDiscount?: boolean;
}

interface ProductAttribute {
  label: string;
  value: string;
}

interface ProductItem {
  productSnapshot: {
    name: string;
    description: string;
  };
  variantSku: string;
  attributes: ProductAttribute[];
  weight: number;
  purchasePrice: number;
  quantity: number;
  total: number;
}

interface OrderProduct {
  product: {
    _id: string;
    name: string;
    sku: string;
    images: string[];
    karat: string;
    weightInGrams: number;
    variantSku: string;
    variantId?: string;
    originalId: string;
    attributeSet: any[];
  };
  purchasePrice: number;
  quantity: number;
  status: string;
  weight: number;
  variantSku: string;
  variantId?: string;
}

interface OrderData {
  _id: string;
  total: number;
  created: string;
  statusHistory: any[];
  paymentMethod: string;
  status: string;
  products: OrderProduct[];
  user: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  purchasePrice: number;
  coupon: string;
  paymentStatus: string;
  paymentId: {
    code: string;
    discountAmount: number;
  };
  address: Address | string;
  orderNotes: string;
  priceBreakdown: {
    [key: string]: PriceBreakdownItem;
  };
  subtotal?: number;
  tax?: number;
  discount?: number;
}

interface InvoiceData {
  _id?: string;
  invoiceNumber: string;
  date: string;
  dueDate?: string;
  paymentStatus: string;
  companyName: string;
  companyAddress: string;
  companyPostalCode: string;
  registrationNumber: string;
  companyEmail: string;
  companyWebsite: string;
  companyPhone: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  products: ProductItem[];
  priceBreakdown: PriceBreakdownItem[];
  subtotal: number;
  totalTax: number;
  discount?: number;
  total: number;
  paymentMethod: string;
  paymentDetails: string;
  paymentId?: string;
  currency: string;
  orderNotes: string;
  clientName?: string;
  clientAddress?: string;
  clientEmail?: string;
  billingAddress?: string;
  shippingAddress?: string;
  gstin?: string;
}

interface InvoiceFormData {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  paymentStatus: string;
  companyName: string;
  companyAddress: string;
  companyPostalCode: string;
  registrationNumber: string;
  companyEmail: string;
  companyWebsite: string;
  companyPhone: string;
  gstin: string;

  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };

  customerAddress: {
    address: string;
    city: string;
    zipCode: string;
    country: string;
    state: string;
  };

  items: {
    id: number;
    productName: string;
    productDetails: string;
    karat: string;
    weight: number;
    pricePerGram: number;
    quantity: number;
    totalPrice: number;
    sku?: string;
  }[];

  priceBreakdownItems: PriceBreakdownItem[];
  subtotal: number;
  totalTax: number;
  discount: number;
  otherCharges: number;
  grandTotal: number;
  finalTotal: number;

  paymentMethod: string;
  paymentDetails: string;
  paymentId: string;

  orderNotes: string;
  currency: string;
}

const CreateInvoice = () => {
  document.title = "Create Invoice";

  const initialFormData: InvoiceFormData = {
    invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
    date: moment().format("D MMM, YYYY"),
    dueDate: moment().add(30, "days").format("D MMM, YYYY"),
    paymentStatus: "Unpaid",

    companyName: "Anjal Gold",
    companyAddress: "",
    companyPostalCode: "",
    registrationNumber: "",
    companyEmail: "",
    companyWebsite: "www.anjalgold.com",
    companyPhone: "",
    gstin: "",

    customer: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },

    customerAddress: {
      address: "",
      city: "",
      zipCode: "",
      country: "India",
      state: "",
    },

    items: [
      {
        id: 1,
        productName: "",
        productDetails: "",
        karat: "",
        weight: 0,
        pricePerGram: 0,
        quantity: 1,
        totalPrice: 0,
      },
    ],

    priceBreakdownItems: [
      { label: "Subtotal (Base Price)", value: 0, type: "subtotal" },
      { label: "Labour Charges", value: 0, type: "charge", isCharge: true },
      { label: "HUID", value: 0, type: "charge", isCharge: true },
      { label: "Total Tax", value: 0, type: "tax" },
      { label: "Grand Total", value: 0, type: "grandTotal" },
      { label: "Final Total", value: 0, type: "total" },
    ],

    subtotal: 0,
    totalTax: 0,
    discount: 0,
    otherCharges: 0,
    grandTotal: 0,
    finalTotal: 0,

    paymentMethod: "Cash",
    paymentDetails: "",
    paymentId: "",

    orderNotes:
      "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged. Please check the items and bill thoroughly at the time of purchase.",

    currency: "₹",
  };

  const [formData, setFormData] = useState<InvoiceFormData>(initialFormData);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [orderId, setOrderId] = useState<string>("");
  const [orders, setOrders] = useState<OrdersResponse>({
    orders: [],
    user: [],
    totalPages: 1,
    currentPage: 1,
    count: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [manualMode, setManualMode] = useState<boolean>(true);
  const [isFromOrder, setIsFromOrder] = useState<boolean>(false);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async (): Promise<void> => {
    try {
      setLoading(true);
      const contactData = await getContactInfo1();

      if (contactData) {
        setContactInfo(contactData);

        const companyAddress = [
          contactData.officeNo,
          contactData.floor,
          contactData.building,
          contactData.landmark,
          contactData.street,
          contactData.area,
          contactData.pincode,
        ]
          .filter(Boolean)
          .join(", ");

        setFormData((prev) => ({
          ...prev,
          companyAddress: companyAddress || prev.companyAddress,
          companyPostalCode: contactData.pincode || prev.companyPostalCode,
          gstin: contactData.gstin || prev.gstin,
          registrationNumber: contactData.gstin || prev.registrationNumber,
          companyEmail: contactData.email || prev.companyEmail,
          companyPhone: contactData.phone || prev.companyPhone,
        }));
      }
    } catch (error: any) {
      console.error("Error fetching contact info:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch contact information");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate item totals only in manual mode
  useEffect(() => {
    if (manualMode && !isFromOrder) {
      const updatedItems = formData.items.map((item) => {
        const totalPrice = item.weight * item.pricePerGram * item.quantity;
        return {
          ...item,
          totalPrice: parseFloat(totalPrice.toFixed(2)),
        };
      });

      const hasChanges = updatedItems.some(
        (item, index) => item.totalPrice !== formData.items[index]?.totalPrice,
      );

      if (hasChanges) {
        setFormData((prev) => ({
          ...prev,
          items: updatedItems,
        }));
      }
    }
  }, [
    manualMode,
    isFromOrder,
    formData.items
      .map((item) => `${item.weight}-${item.pricePerGram}-${item.quantity}`)
      .join(","),
  ]);

  // Calculate all totals and price breakdown only in manual mode
  useEffect(() => {
    if (manualMode && !isFromOrder) {
      // Calculate subtotal from items
      const subtotal = formData.items.reduce(
        (sum, item) => sum + item.totalPrice,
        0,
      );

      // Get values from price breakdown items
      let labourCharges = 0;
      let huid = 0;
      let taxAmount = 0;
      let discountAmount = 0;

      formData.priceBreakdownItems.forEach((item) => {
        if (item.label === "Labour Charges") {
          labourCharges = item.value;
        } else if (item.label === "HUID") {
          huid = item.value;
        } else if (item.type === "tax") {
          taxAmount = item.value;
        } else if (item.type === "discount") {
          discountAmount = item.value;
        }
      });

      const otherCharges = labourCharges + huid;
      const grandTotal = subtotal + otherCharges + taxAmount;
      const finalTotal = grandTotal - discountAmount;

      // Update price breakdown items with calculated values
      const updatedPriceBreakdown = formData.priceBreakdownItems.map((item) => {
        if (item.label === "Subtotal (Base Price)") {
          return { ...item, value: subtotal };
        } else if (item.label === "Grand Total") {
          return { ...item, value: grandTotal };
        } else if (item.label === "Final Total") {
          return { ...item, value: finalTotal };
        }
        return item;
      });

      setFormData((prev) => ({
        ...prev,
        subtotal,
        totalTax: taxAmount,
        discount: discountAmount,
        otherCharges,
        grandTotal,
        finalTotal,
        priceBreakdownItems: updatedPriceBreakdown,
      }));
    }
  }, [manualMode, isFromOrder, formData.items, formData.priceBreakdownItems]);

  // Handle form field changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { id, value } = e.target;

    if (id.startsWith("customer.")) {
      const field = id.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        customer: {
          ...prev.customer,
          [field]: value,
        },
      }));
    } else if (id.startsWith("customerAddress.")) {
      const field = id.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        customerAddress: {
          ...prev.customerAddress,
          [field]: value,
        },
      }));
    } else if (
      id === "subtotal" ||
      id === "totalTax" ||
      id === "discount" ||
      id === "otherCharges" ||
      id === "grandTotal" ||
      id === "finalTotal"
    ) {
      if (manualMode && !isFromOrder) {
        const numValue = parseFloat(value) || 0;
        setFormData((prev) => {
          const newData = { ...prev, [id]: numValue };

          // Update price breakdown when manual totals change
          if (id === "subtotal") {
            newData.priceBreakdownItems = prev.priceBreakdownItems.map(
              (item) =>
                item.label === "Subtotal (Base Price)"
                  ? { ...item, value: numValue }
                  : item,
            );
          } else if (id === "totalTax") {
            newData.priceBreakdownItems = prev.priceBreakdownItems.map(
              (item) =>
                item.type === "tax" ? { ...item, value: numValue } : item,
            );
          } else if (id === "discount") {
            newData.priceBreakdownItems = prev.priceBreakdownItems.map(
              (item) =>
                item.type === "discount" ? { ...item, value: numValue } : item,
            );
          } else if (id === "grandTotal") {
            newData.priceBreakdownItems = prev.priceBreakdownItems.map(
              (item) =>
                item.label === "Grand Total"
                  ? { ...item, value: numValue }
                  : item,
            );
          } else if (id === "finalTotal") {
            newData.priceBreakdownItems = prev.priceBreakdownItems.map(
              (item) =>
                item.label === "Final Total"
                  ? { ...item, value: numValue }
                  : item,
            );
          }

          return newData;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  // Handle price breakdown item changes
  const handlePriceBreakdownChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    if (isFromOrder) {
      const item = formData.priceBreakdownItems[index];
      if (item.type === "grandTotal" || item.type === "total") {
        return; // Don't allow editing grandTotal and total when from order
      }
    }

    const numValue = typeof value === "string" ? parseFloat(value) || 0 : value;

    setFormData((prev) => {
      const updatedBreakdown = [...prev.priceBreakdownItems];
      updatedBreakdown[index] = {
        ...updatedBreakdown[index],
        [field]: field === "label" ? value : numValue,
      };

      return {
        ...prev,
        priceBreakdownItems: updatedBreakdown,
      };
    });
  };

  // Add new price breakdown item
  const addPriceBreakdownItem = () => {
    setFormData((prev) => ({
      ...prev,
      priceBreakdownItems: [
        ...prev.priceBreakdownItems.filter(
          (item) => item.type !== "grandTotal" && item.type !== "total",
        ),
        { label: "New Charge", value: 0, type: "charge", isCharge: true },
        {
          label: "Grand Total",
          value: prev.grandTotal,
          type: "grandTotal",
        },
        {
          label: "Final Total",
          value: prev.finalTotal,
          type: "total",
        },
      ],
    }));
  };

  // Remove price breakdown item
  const removePriceBreakdownItem = (index: number) => {
    const item = formData.priceBreakdownItems[index];
    if (item.type === "grandTotal" || item.type === "total") {
      return; // Don't remove grandTotal and total
    }

    if (formData.priceBreakdownItems.length <= 3) return; // Keep at least subtotal + grandTotal + finalTotal

    setFormData((prev) => ({
      ...prev,
      priceBreakdownItems: prev.priceBreakdownItems.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  // Handle item field changes
  const handleItemChange = (
    id: number,
    field: string,
    value: string | number,
  ) => {
    if (isFromOrder) return; // Don't allow editing items when loaded from order

    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                typeof value === "string" &&
                field !== "productName" &&
                field !== "productDetails" &&
                field !== "karat"
                  ? field === "quantity"
                    ? parseInt(value) || 1
                    : parseFloat(value) || 0
                  : value,
            }
          : item,
      ),
    }));
  };

  const addNewItem = () => {
    if (isFromOrder) return; // Don't allow adding items when loaded from order

    const newId =
      formData.items.length > 0
        ? Math.max(...formData.items.map((item) => item.id)) + 1
        : 1;

    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: newId,
          productName: "",
          productDetails: "",
          karat: "",
          weight: 0,
          pricePerGram: 0,
          quantity: 1,
          totalPrice: 0,
        },
      ],
    }));
  };

  const removeItem = (id: number) => {
    if (isFromOrder) {
      toast.warning("Cannot remove items when loaded from order");
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning("At least one item is required");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  // Prepare invoice data for PDF generation
  const prepareInvoiceData = (): InvoiceData => {
    const products: ProductItem[] = formData.items.map((item, index) => ({
      productSnapshot: {
        name: item.productName || "Product",
        description: item.productDetails || "",
      },
      variantSku: item.sku || `SKU-${index + 1}`,
      attributes: [
        ...(item.karat ? [{ label: "Karat", value: item.karat }] : []),
        ...(item.weight
          ? [{ label: "Weight", value: item.weight.toString() }]
          : []),
      ],
      weight: item.weight,
      purchasePrice: item.totalPrice,
      quantity: item.quantity,
      total: item.totalPrice,
    }));

    return {
      _id: orderId || `INV-${Date.now()}`,
      invoiceNumber: formData.invoiceNumber,
      date: formData.date,
      dueDate: formData.dueDate,
      paymentStatus: formData.paymentStatus,

      companyName: formData.companyName,
      companyAddress: formData.companyAddress,
      companyPostalCode: formData.companyPostalCode,
      registrationNumber: formData.registrationNumber,
      companyEmail: formData.companyEmail,
      companyWebsite: formData.companyWebsite,
      companyPhone: formData.companyPhone,

      firstName: formData.customer.firstName,
      lastName: formData.customer.lastName,
      address: formData.customerAddress.address,
      city: formData.customerAddress.city,
      zipCode: formData.customerAddress.zipCode,
      country: formData.customerAddress.country,
      phone: formData.customer.phone,
      email: formData.customer.email,

      products,
      priceBreakdown: formData.priceBreakdownItems,
      subtotal: formData.subtotal,
      totalTax: formData.totalTax,
      discount: formData.discount,
      total: formData.finalTotal,

      paymentMethod: formData.paymentMethod,
      paymentDetails: formData.paymentDetails,
      paymentId: formData.paymentId,

      currency: formData.currency,
      orderNotes: formData.orderNotes,

      // For email
      clientName:
        `${formData.customer.firstName} ${formData.customer.lastName}`.trim(),
      clientAddress: formData.customerAddress.address,
      clientEmail: formData.customer.email,
      billingAddress: formData.customerAddress.address,
      shippingAddress: formData.customerAddress.address,
      gstin: formData.gstin,
    };
  };

  const handleDownloadInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();

    try {
      const invoiceData = prepareInvoiceData();
      console.log("Sending invoice data:", invoiceData);
      await generateInvoicePDF(invoiceData);
      toast.success("Invoice generated successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate invoice. Please try again.");
    }
  };

  const handleSendInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSending(true);
    setSendStatus(null);

    try {
      const invoiceData = prepareInvoiceData();

      if (!invoiceData.clientEmail) {
        throw new Error("Customer email is required");
      }

      const result = await sendInvoiceEmail({
        invoiceData,
        to: invoiceData.clientEmail,
        subject: `Invoice ${invoiceData.invoiceNumber} from Anjal Gold`,
        message: `Please find attached invoice #${invoiceData.invoiceNumber} for your records.`,
      });

      setSendStatus({
        success: true,
        message: `Invoice sent successfully to ${invoiceData.clientEmail}`,
      });
      toast.success("Invoice sent successfully!");
    } catch (error) {
      console.error("Error sending invoice:", error);
      setSendStatus({
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to send invoice",
      });
      toast.error("Failed to send invoice");
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();

    try {
      const invoiceData = prepareInvoiceData();

      // Call the save invoice API
      const result = await saveInvoice(invoiceData);

      setSendStatus({
        success: true,
        message: "Invoice saved successfully",
      });
      toast.success("Invoice saved successfully!");
    } catch (error: any) {
      console.error("Error saving invoice:", error);
      setSendStatus({
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to save invoice",
      });
      toast.error(error.response.data.message);
    }
  };

  const loadOrders = async (page: number = 1) => {
    try {
      setLoading(true);
      setError("");
      setManualMode(false);
      setIsFromOrder(false);
      const data = await fetchOrders(page, 100);
      setOrders(data);
      toast.success("Orders loaded successfully!");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load orders");
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSelect = async (selectedOrderId: string) => {
    try {
      setLoading(true);
      setOrderId(selectedOrderId);
      setManualMode(false);
      setIsFromOrder(true);

      const orderData = await getOrderById(selectedOrderId);
      console.log("Loaded order data:", orderData);

      if (!orderData) {
        toast.error("Order not found");
        return;
      }

      // Extract customer information
      let firstName = "";
      let lastName = "";
      let phone = "";
      let email = "";
      let address = "";
      let city = "";
      let zipCode = "";
      let country = "";
      let state = "";

      // Extract user info
      if (orderData.user) {
        firstName = orderData.user.firstName || "";
        lastName = orderData.user.lastName || "";
        email = orderData.user.email || "";
      }

      // Extract address info
      if (orderData.address) {
        if (typeof orderData.address === "string") {
          address = orderData.address;
        } else {
          const addrObj = orderData.address as unknown as Address;
          firstName = addrObj.firstName || firstName;
          lastName = addrObj.lastName || lastName;
          phone = addrObj.phone || "";
          address = addrObj.address || "";
          city = addrObj.city || "";
          zipCode = addrObj.zipCode || addrObj.zipcode || "";
          country = addrObj.country || "";
          state = addrObj.state || "";
        }
      }

      // Extract products
      const items: any[] = [];
      if (orderData.products && Array.isArray(orderData.products)) {
        orderData.products.forEach((productItem, index) => {
          const product = productItem.product;
          const weight = productItem.weight || product?.weightInGrams || 0;
          const quantity = productItem.quantity || 1;
          const totalPrice = productItem.purchasePrice || 0;
          const pricePerGram = weight > 0 ? totalPrice / weight : 0;

          items.push({
            id: index + 1,
            productName: product?.name || "Product",
            productDetails: product?.karat ? `${product.karat} Gold` : "",
            karat: product?.karat || "",
            weight: weight,
            pricePerGram: pricePerGram,
            quantity: quantity,
            totalPrice: totalPrice,
            sku: product?.sku || `SKU-${index + 1}`,
          });
        });
      }

      // Extract price breakdown
      const priceBreakdownItems: PriceBreakdownItem[] = [];
      let subtotal = 0;
      let totalTax = 0;
      let discount = 0;
      let otherCharges = 0;
      let grandTotal = orderData.total || 0;
      let finalTotal = orderData.total || 0;

      if (orderData.priceBreakdown) {
        Object.entries(orderData.priceBreakdown).forEach(
          ([key, item]: [string, any]) => {
            if (item.show !== false) {
              let type = item.type;

              // Map types to simplified ones
              if (item.isCharge) {
                type = "charge";
              } else if (type === "number") {
                type = item.isCharge ? "charge" : type;
              }

              // Skip grandTotal and total from breakdown as they're calculated totals
              if (type === "grandTotal" || type === "total") {
                // We'll add these at the end as read-only
                return;
              }

              const breakdownItem: PriceBreakdownItem = {
                label: item.label,
                value: item.value,
                type: type,
                show: item.show,
                valueType: item.valueType,
                isCharge: item.isCharge,
                isDiscount: item.type === "discount",
              };

              priceBreakdownItems.push(breakdownItem);

              // Just record values, don't calculate
              if (type === "subtotal") {
                subtotal = item.value;
              } else if (type === "tax") {
                totalTax = item.value;
              } else if (type === "discount") {
                discount = item.value;
              } else if (type === "charge") {
                otherCharges += item.value;
              }
            }
          },
        );

        // Add grand total and final total as read-only fields
        priceBreakdownItems.push(
          {
            label: "Grand Total",
            value: orderData.total || 0,
            type: "grandTotal",
            show: true,
          },
          {
            label: "Final Total",
            value: orderData.total || 0,
            type: "total",
            show: true,
          },
        );
      } else {
        // Fallback: Use order total directly
        subtotal = orderData.total || 0;
        grandTotal = orderData.total || 0;
        finalTotal = orderData.total || 0;

        priceBreakdownItems.push(
          {
            label: "Subtotal (Base Price)",
            value: subtotal,
            type: "subtotal",
          },
          {
            label: "Grand Total",
            value: grandTotal,
            type: "grandTotal",
          },
          {
            label: "Final Total",
            value: finalTotal,
            type: "total",
          },
        );
      }

      // Update form data with EXACT values from order
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
        date: moment(orderData.created).format("D MMM, YYYY"),
        paymentStatus: orderData.paymentStatus === "paid" ? "Paid" : "Unpaid",

        customer: {
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
        },

        customerAddress: {
          address: address,
          city: city,
          zipCode: zipCode,
          country: country || "India",
          state: state,
        },

        items: items,
        priceBreakdownItems: priceBreakdownItems,
        subtotal: subtotal,
        totalTax: totalTax,
        discount: discount,
        otherCharges: otherCharges,
        grandTotal: grandTotal,
        finalTotal: finalTotal,

        paymentMethod:
          orderData.paymentMethod === "card"
            ? "Online"
            : orderData.paymentMethod === "cash"
              ? "Cash"
              : orderData.paymentMethod || "Cash",

        orderNotes: orderData.orderNotes || prev.orderNotes,
      }));

      toast.success("Order loaded successfully!");
    } catch (error) {
      console.error("Error loading order details:", error);
      toast.error("Failed to load order details");
      setError(error instanceof Error ? error.message : "Failed to load order");
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setFormData({
      ...initialFormData,
      companyAddress: contactInfo
        ? [
            contactInfo.officeNo,
            contactInfo.floor,
            contactInfo.building,
            contactInfo.landmark,
            contactInfo.street,
            contactInfo.area,
            contactInfo.pincode,
          ]
            .filter(Boolean)
            .join(", ")
        : "",
      companyPostalCode: contactInfo?.pincode || "",
      gstin: contactInfo?.gstin || "",
      registrationNumber: contactInfo?.gstin || "",
      companyEmail: contactInfo?.email || "",
      companyPhone: contactInfo?.phone || "",
    });
    setOrderId("");
    setSendStatus(null);
    setManualMode(true);
    setIsFromOrder(false);
    toast.info("Form cleared");
  };

  const switchToManualMode = () => {
    setManualMode(true);
    setIsFromOrder(false);
    setOrderId("");
    toast.info("Switched to manual invoice creation");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumb title="Create Invoice" pageTitle="Invoices" />

          <Row className="justify-content-center">
            <Col xxl={9}>
              <Card>
                <Form className="needs-validation" id="invoice_form">
                  <Card.Body className="border-bottom border-bottom-dashed p-4">
                    <Row>
                      <Col lg={4}>
                        <div className="profile-user mx-auto  mb-3">
                          {/* <input
                            id="profile-img-file-input"
                            type="file"
                            className="profile-img-file-input"
                          /> */}
                          <label
                            htmlFor="profile-img-file-input"
                            className="d-block"
                          >
                            <span
                              className="overflow-hidden border border-dashed d-flex align-items-center justify-content-center rounded"
                              style={{ height: "150px", width: "256px" }}
                            >
                              <img
                                src={logoDark}
                                className="card-logo card-logo-dark user-profile-image img-fluid"
                                alt="logo dark"
                              />
                              <img
                                src={logoLight}
                                className="card-logo card-logo-light user-profile-image img-fluid"
                                alt="logo light"
                              />
                            </span>
                          </label>
                        </div>
                        <div>
                          <Form.Label
                            htmlFor="companyAddress"
                            className="form-label"
                          >
                            Company Address
                          </Form.Label>
                          <div className="mb-2">
                            <Form.Control
                              as="textarea"
                              id="companyAddress"
                              placeholder="Company Address"
                              rows={3}
                              value={formData.companyAddress}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div className="mb-2 mb-lg-0">
                            <Form.Control
                              type="text"
                              id="companyPostalCode"
                              placeholder="Enter Postal Code"
                              value={formData.companyPostalCode}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col lg={4} className="ms-auto">
                        <div className="mb-2">
                          <Form.Control
                            type="text"
                            id="gstin"
                            placeholder="GST No"
                            value={formData.gstin}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="mb-2">
                          <Form.Control
                            type="email"
                            id="companyEmail"
                            placeholder="Email Address"
                            value={formData.companyEmail}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="mb-2">
                          <Form.Control
                            type="text"
                            id="companyWebsite"
                            placeholder="Website"
                            value={formData.companyWebsite}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div>
                          <Form.Control
                            type="tel"
                            id="companyPhone"
                            placeholder="Contact No"
                            value={formData.companyPhone}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>

                  <Card.Body className="p-4">
                    <Row className="g-3">
                      <Col lg={3} sm={6}>
                        <Form.Label htmlFor="invoiceNumber">
                          Invoice No
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="invoiceNumber"
                          placeholder="Invoice No"
                          value={formData.invoiceNumber}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col lg={3} sm={6}>
                        <div>
                          <Form.Label htmlFor="date">Date</Form.Label>
                          <Flatpickr
                            className="form-control flatpickr-input"
                            placeholder="Select Date-time"
                            options={{ dateFormat: "d M, Y" }}
                            value={formData.date}
                            onChange={([date]) => {
                              setFormData((prev) => ({
                                ...prev,
                                date: moment(date).format("D MMM, YYYY"),
                              }));
                            }}
                          />
                        </div>
                      </Col>
                      <Col lg={3} sm={6}>
                        <Form.Label htmlFor="paymentStatus">
                          Payment Status
                        </Form.Label>
                        <Form.Select
                          id="paymentStatus"
                          value={formData.paymentStatus}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Payment Status</option>
                          <option value="Paid">Paid</option>
                          <option value="Unpaid">Unpaid</option>
                          <option value="Refund">Refund</option>
                        </Form.Select>
                      </Col>
                      <Col lg={3} sm={6}>
                        <div>
                          <Form.Label htmlFor="finalTotal">
                            Final Total
                          </Form.Label>
                          <Form.Control
                            type="number"
                            id="finalTotal"
                            placeholder="₹0.00"
                            value={formData.finalTotal}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>

                  {/* Order Selection / Manual Mode */}
                  <Card.Body className="p-4 border-top border-top-dashed">
                    <Row>
                      <Col lg={12}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="text-muted text-uppercase fw-semibold mb-0">
                            {manualMode
                              ? "Manual Invoice Creation"
                              : "Order Selection"}
                          </h5>
                          <div className="hstack gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={switchToManualMode}
                              disabled={manualMode}
                            >
                              Create Manual Invoice
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => loadOrders(1)}
                              disabled={loading}
                            >
                              {loading ? "Loading..." : "Load Orders"}
                            </Button>
                          </div>
                        </div>

                        {!manualMode && orders.orders.length > 0 && (
                          <div className="mb-3">
                            <Form.Label htmlFor="orderSelect">
                              Select Order
                            </Form.Label>
                            <Select
                              options={orders.orders.map((order) => ({
                                value: order._id,
                                label: `Order #${order._id.slice(-6)} - ${order.user?.firstName || ""} ${order.user?.lastName || ""} - ₹${order.total || 0}`,
                              }))}
                              value={
                                orderId
                                  ? {
                                      value: orderId,
                                      label: `Order #${orderId.slice(-6)}`,
                                    }
                                  : null
                              }
                              onChange={(selected) => {
                                if (selected) {
                                  handleOrderSelect(selected.value);
                                }
                              }}
                              placeholder="Select an order to load..."
                              styles={{
                                menuList: (base) => ({
                                  ...base,
                                  maxHeight: "300px",
                                }),
                              }}
                              isLoading={loading}
                            />
                          </div>
                        )}

                        {error && (
                          <div className="text-danger mb-3">{error}</div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>

                  {/* Customer Information */}
                  <Card.Body className="p-4 border-top border-top-dashed">
                    <Row>
                      <Col lg={6}>
                        <h5 className="text-muted text-uppercase fw-semibold mb-3">
                          Customer Information
                        </h5>
                        <div className="mb-3">
                          <Form.Label htmlFor="customer.firstName">
                            First Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            id="customer.firstName"
                            placeholder="First Name"
                            value={formData.customer.firstName}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                        <div className="mb-3">
                          <Form.Label htmlFor="customer.lastName">
                            Last Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            id="customer.lastName"
                            placeholder="Last Name"
                            value={formData.customer.lastName}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                        <div className="mb-3">
                          <Form.Label htmlFor="customer.email">
                            Email Address
                          </Form.Label>
                          <Form.Control
                            type="email"
                            id="customer.email"
                            placeholder="customer@example.com"
                            value={formData.customer.email}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                        <div className="mb-3">
                          <Form.Label htmlFor="customer.phone">
                            Phone Number
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            id="customer.phone"
                            placeholder="Phone Number"
                            value={formData.customer.phone}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                      </Col>

                      <Col lg={6}>
                        <h5 className="text-muted text-uppercase fw-semibold mb-3">
                          Customer Address
                        </h5>
                        <div className="mb-3">
                          <Form.Label htmlFor="customerAddress.address">
                            Street Address
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            id="customerAddress.address"
                            placeholder="Street Address"
                            rows={3}
                            value={formData.customerAddress.address}
                            onChange={handleInputChange}
                            disabled={isFromOrder}
                          />
                        </div>
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Form.Label htmlFor="customerAddress.city">
                                City
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="customerAddress.city"
                                placeholder="City"
                                value={formData.customerAddress.city}
                                onChange={handleInputChange}
                                disabled={isFromOrder}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Form.Label htmlFor="customerAddress.zipCode">
                                Postal Code
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="customerAddress.zipCode"
                                placeholder="Postal Code"
                                value={formData.customerAddress.zipCode}
                                onChange={handleInputChange}
                                disabled={isFromOrder}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Form.Label htmlFor="customerAddress.state">
                                State
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="customerAddress.state"
                                placeholder="State"
                                value={formData.customerAddress.state}
                                onChange={handleInputChange}
                                disabled={isFromOrder}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Form.Label htmlFor="customerAddress.country">
                                Country
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="customerAddress.country"
                                placeholder="Country"
                                value={formData.customerAddress.country}
                                onChange={handleInputChange}
                                disabled={isFromOrder}
                              />
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>

                  {/* Price Breakdown Section */}
                  <Card.Body className="p-4 border-top border-top-dashed">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="text-muted text-uppercase fw-semibold mb-0">
                        Price Breakdown
                      </h5>
                      {!isFromOrder && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={addPriceBreakdownItem}
                        >
                          <i className="ri-add-line me-1"></i> Add
                          Charge/Discount
                        </Button>
                      )}
                    </div>

                    <Table className="table-sm mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "40%" }}>Description</th>
                          <th style={{ width: "30%" }}>Type</th>
                          <th style={{ width: "20%" }}>Amount (₹)</th>
                          <th style={{ width: "10%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.priceBreakdownItems.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.label}
                                onChange={(e) =>
                                  handlePriceBreakdownChange(
                                    index,
                                    "label",
                                    e.target.value,
                                  )
                                }
                                size="sm"
                                disabled={
                                  item.type === "grandTotal" ||
                                  item.type === "total"
                                }
                              />
                            </td>
                            <td>
                              <Form.Select
                                value={item.type}
                                onChange={(e) =>
                                  handlePriceBreakdownChange(
                                    index,
                                    "type",
                                    e.target.value,
                                  )
                                }
                                size="sm"
                                disabled={
                                  item.type === "grandTotal" ||
                                  item.type === "total"
                                }
                              >
                                <option value="subtotal">Subtotal</option>
                                <option value="tax">Tax</option>
                                <option value="charge">Charge</option>
                                <option value="discount">Discount</option>
                                <option value="grandTotal" disabled>
                                  Grand Total
                                </option>
                                <option value="total" disabled>
                                  Final Total
                                </option>
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={item.value}
                                onChange={(e) =>
                                  handlePriceBreakdownChange(
                                    index,
                                    "value",
                                    e.target.value,
                                  )
                                }
                                size="sm"
                                step="0.01"
                                disabled={
                                  item.type === "grandTotal" ||
                                  item.type === "total"
                                }
                              />
                            </td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removePriceBreakdownItem(index)}
                                disabled={
                                  formData.priceBreakdownItems.length <= 3 ||
                                  item.type === "grandTotal" ||
                                  item.type === "total"
                                }
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Summary Totals */}
                    <div className="mt-4 border-top pt-3">
                      <Row>
                        <Col lg={6}>
                          <Table className="table-borderless table-sm">
                            <tbody>
                              <tr>
                                <th>Subtotal (Base Price):</th>
                                <td className="text-end">
                                  ₹{formData.subtotal.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th>Labour Charges:</th>
                                <td className="text-end">
                                  ₹
                                  {formData.priceBreakdownItems
                                    .filter(
                                      (item) => item.label === "Labour Charges",
                                    )
                                    .reduce((sum, item) => sum + item.value, 0)
                                    .toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th>HUID:</th>
                                <td className="text-end">
                                  ₹
                                  {formData.priceBreakdownItems
                                    .filter((item) => item.label === "HUID")
                                    .reduce((sum, item) => sum + item.value, 0)
                                    .toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th>Total Tax:</th>
                                <td className="text-end">
                                  ₹{formData.totalTax.toFixed(2)}
                                </td>
                              </tr>
                              <tr className="border-top">
                                <th>
                                  <strong>Grand Total:</strong>
                                </th>
                                <td className="text-end">
                                  <strong>
                                    ₹{formData.grandTotal.toFixed(2)}
                                  </strong>
                                </td>
                              </tr>
                              <tr>
                                <th>Discount:</th>
                                <td className="text-end text-danger">
                                  -₹{formData.discount.toFixed(2)}
                                </td>
                              </tr>
                              <tr className="border-top">
                                <th>
                                  <strong>Final Total:</strong>
                                </th>
                                <td className="text-end">
                                  <strong>
                                    ₹{formData.finalTotal.toFixed(2)}
                                  </strong>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>

                  {/* Products Section */}
                  <Card.Body className="p-4">
                    <div className="table-responsive">
                      <Table className="invoice-table table-borderless table-nowrap mb-0">
                        <thead className="align-middle">
                          <tr className="table-active">
                            <th style={{ width: "50px" }}>#</th>
                            <th style={{ width: "40%" }}>Product Details</th>
                            <th style={{ width: "120px" }}>Karat</th>
                            <th style={{ width: "120px" }}>Weight (grams)</th>
                            <th style={{ width: "120px" }}>Price</th>
                            <th style={{ width: "120px" }}>Quantity</th>
                            <th className="text-end" style={{ width: "180px" }}>
                              Total Price
                            </th>
                            <th
                              className="text-end"
                              style={{ width: "105px" }}
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={item.id} className="product">
                              <th scope="row">{index + 1}</th>
                              <td className="text-start">
                                <div className="mb-2">
                                  <Form.Control
                                    type="text"
                                    className="product-name"
                                    placeholder="Product Name"
                                    value={item.productName}
                                    onChange={(e) =>
                                      handleItemChange(
                                        item.id,
                                        "productName",
                                        e.target.value,
                                      )
                                    }
                                    disabled={isFromOrder}
                                  />
                                </div>
                                <Form.Control
                                  as="textarea"
                                  className="product-details"
                                  placeholder="Product Details"
                                  value={item.productDetails}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      "productDetails",
                                      e.target.value,
                                    )
                                  }
                                  rows={2}
                                  disabled={isFromOrder}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  className="product-karat"
                                  placeholder="22K"
                                  value={item.karat}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      "karat",
                                      e.target.value,
                                    )
                                  }
                                  disabled={isFromOrder}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  className="product-weight"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={item.weight || ""}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      "weight",
                                      e.target.value,
                                    )
                                  }
                                  disabled={isFromOrder}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  className="product-price"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={item.totalPrice || ""}
                                  onChange={(e) =>
                                    handleItemChange(
                                      item.id,
                                      "totalPrice",
                                      e.target.value,
                                    )
                                  }
                                  disabled={isFromOrder}
                                />
                              </td>
                              <td>
                                <div className="input-step">
                                  <Button
                                    className="minus"
                                    onClick={() =>
                                      handleItemChange(
                                        item.id,
                                        "quantity",
                                        Math.max(1, item.quantity - 1),
                                      )
                                    }
                                    disabled={isFromOrder}
                                  >
                                    –
                                  </Button>
                                  <input
                                    type="number"
                                    className="product-quantity"
                                    value={item.quantity}
                                    min="1"
                                    onChange={(e) =>
                                      handleItemChange(
                                        item.id,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                    disabled={isFromOrder}
                                  />
                                  <Button
                                    className="plus"
                                    onClick={() =>
                                      handleItemChange(
                                        item.id,
                                        "quantity",
                                        item.quantity + 1,
                                      )
                                    }
                                    disabled={isFromOrder}
                                  >
                                    +
                                  </Button>
                                </div>
                              </td>
                              <td className="text-end">
                                <Form.Control
                                  type="number"
                                  className="product-line-price"
                                  placeholder="₹0.00"
                                  value={item.totalPrice || ""}
                                  readOnly
                                />
                              </td>
                              <td>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => removeItem(item.id)}
                                  disabled={
                                    formData.items.length <= 1 || isFromOrder
                                  }
                                >
                                  Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tbody>
                          <tr>
                            <td colSpan={8}>
                              {!isFromOrder && (
                                <Button
                                  variant="soft-secondary"
                                  className="fw-medium"
                                  onClick={addNewItem}
                                >
                                  <i className="ri-add-fill me-1 align-bottom"></i>{" "}
                                  Add Item
                                </Button>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    {/* Payment and Notes */}
                    <Row className="mt-4">
                      <Col lg={6}>
                        <div className="mb-3">
                          <Form.Label
                            htmlFor="paymentMethod"
                            className="text-muted text-uppercase fw-semibold"
                          >
                            Payment Mode
                          </Form.Label>
                          <Form.Select
                            id="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleInputChange}
                          >
                            <option value="">Payment Method</option>
                            <option value="Cash">Cash</option>
                            <option value="Online">Online</option>
                          </Form.Select>
                        </div>
                        {/* <div className="mb-3">
                          <Form.Control
                            type="text"
                            id="paymentDetails"
                            placeholder="Payment Details (e.g. UPI ID, Cheque No)"
                            value={formData.paymentDetails}
                            onChange={handleInputChange}
                          />
                        </div> */}
                      </Col>
                    </Row>

                    <div className="mt-4">
                      <Form.Label
                        htmlFor="orderNotes"
                        className="text-muted text-uppercase fw-semibold"
                      >
                        NOTES
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        className="alert alert-warning"
                        id="orderNotes"
                        placeholder="Notes"
                        value={formData.orderNotes}
                        onChange={handleInputChange}
                        rows={3}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="hstack gap-2 justify-content-end d-print-none mt-4">
                      <Button variant="success" onClick={handleSaveInvoice}>
                        <i className="ri-printer-line align-bottom me-1"></i>{" "}
                        Save Invoice
                      </Button>
                      <Button variant="primary" onClick={handleDownloadInvoice}>
                        <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                        Download Invoice
                      </Button>
                      <Button
                        variant="danger"
                        onClick={handleSendInvoice}
                        disabled={isSending || !formData.customer.email}
                      >
                        {isSending ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-fill align-bottom me-1"></i>
                            Send Invoice
                          </>
                        )}
                      </Button>
                      <Button variant="secondary" onClick={clearForm}>
                        <i className="ri-close-line align-bottom me-1"></i>{" "}
                        Clear Form
                      </Button>
                      {sendStatus && (
                        <div
                          className={`alert alert-${sendStatus.success ? "success" : "danger"} mt-3`}
                        >
                          {sendStatus.message}
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Form>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CreateInvoice;
